import { useState, useMemo, useCallback } from 'react'
import type {
  CotizacionTarea,
  ConfigTarifasCotizacion,
  PerfilEquipo,
  CostoLineItem,
} from '@/lib/types'
import { r2 } from '@/lib/utils'
import {
  IconSettings,
  IconDollar,
  IconChevronDown,
  IconChevronRight,
  IconTrendingUp,
  IconPlus,
  IconTrash,
} from '@/components/icons/Icons'

/* ================================================================
   PresupuestoTab — Budget tab with per-task dynamic cost items.
   Each task has its own expandable cost box with add/remove items.
   ================================================================ */

interface Props {
  tareas: CotizacionTarea[]
  setTareas: React.Dispatch<React.SetStateAction<CotizacionTarea[]>>
  config: ConfigTarifasCotizacion
  setConfig: React.Dispatch<React.SetStateAction<ConfigTarifasCotizacion>>
  perfiles: PerfilEquipo[]
  moneda: string
  formatCurrency: (n: number, m: string) => string
  subtotal: number
  igvMonto: number
  descMonto: number
  total: number
  igvPct: number
  descPct: number
}

// ─── Preset cost concepts (non-operario) ───
const PRESETS: { concepto: string; unidad: string; tarifaKey?: keyof ConfigTarifasCotizacion }[] = [
  { concepto: 'Tokens Input', unidad: 'MTok', tarifaKey: 'precio_input_mtok' },
  { concepto: 'Tokens Thinking', unidad: 'MTok', tarifaKey: 'precio_thinking_mtok' },
  { concepto: 'Tokens Output', unidad: 'MTok', tarifaKey: 'precio_output_mtok' },
  { concepto: 'Licencia', unidad: 'unidades' },
  { concepto: 'Infraestructura', unidad: 'unidades' },
]

function createLineItem(concepto: string, unidad: string, tarifa: number): CostoLineItem {
  return {
    id: crypto.randomUUID(),
    concepto,
    cantidad: 0,
    unidad,
    tarifa,
    subtotal: 0,
  }
}

// ─── Component ──────────────────────────────────────────────────

export default function PresupuestoTab({
  tareas,
  setTareas,
  config,
  setConfig,
  perfiles,
  moneda,
  formatCurrency,
  subtotal,
  igvMonto,
  descMonto,
  total,
  igvPct,
  descPct,
}: Props) {
  const [tarifasOpen, setTarifasOpen] = useState(false)
  const [expandedTarea, setExpandedTarea] = useState<number | null>(null)
  const [showPresets, setShowPresets] = useState<number | null>(null)
  const [showOperarios, setShowOperarios] = useState(false)

  // ── Config change ──
  const onConfigChange = useCallback(
    (field: keyof ConfigTarifasCotizacion, raw: string) => {
      const val = parseFloat(raw) || 0
      setConfig((prev) => ({ ...prev, [field]: val }))

      // When margen_global changes, recalculate montos for tasks that have cost items
      if (field === 'margen_global') {
        const newMargen = val
        const divisor = 1 - newMargen / 100
        setTareas((prev) =>
          prev.map((t) => {
            const items = t.costos_items ?? []
            if (items.length === 0) return t  // Don't touch tasks without cost items
            const costoTotal = r2(items.reduce((s, c) => s + c.subtotal, 0))
            const monto = divisor > 0 ? r2(costoTotal / divisor) : costoTotal
            return { ...t, monto }
          }),
        )
      }
    },
    [setConfig, setTareas],
  )

  // ── Fill tarifa from profile chip ──
  const fillTarifa = useCallback(
    (perfil: PerfilEquipo) => {
      const isQA = perfil.id_perfil.toLowerCase().includes('qa') || perfil.rol_comercial.toLowerCase().includes('qa')
      if (isQA) {
        setConfig((prev) => ({ ...prev, tarifa_qa_hora: perfil.costo_interno_hora }))
      } else {
        setConfig((prev) => ({ ...prev, tarifa_dev_hora: perfil.costo_interno_hora }))
      }
    },
    [setConfig],
  )

  // ── Add cost item to a task ──
  const addCostoItem = useCallback(
    (tareaIdx: number, concepto: string, unidad: string, tarifaKey?: keyof ConfigTarifasCotizacion) => {
      const tarifa = tarifaKey ? (config[tarifaKey] as number) : 0
      const item = createLineItem(concepto, unidad, tarifa)
      setTareas((prev) => prev.map((t, i) => {
        if (i !== tareaIdx) return t
        const items = [...(t.costos_items ?? []), item]
        return { ...t, costos_items: items }
      }))
      setShowPresets(null)
    },
    [config, setTareas],
  )

  // ── Add operario (profile) cost item ──
  const addOperarioItem = useCallback(
    (tareaIdx: number, perfil: PerfilEquipo) => {
      const concepto = `${perfil.rol_comercial} ${perfil.seniority}`
      const item = createLineItem(concepto, 'horas', perfil.costo_interno_hora)
      setTareas((prev) => prev.map((t, i) => {
        if (i !== tareaIdx) return t
        const items = [...(t.costos_items ?? []), item]
        return { ...t, costos_items: items }
      }))
      setShowPresets(null)
      setShowOperarios(false)
    },
    [setTareas],
  )

  // ── Add custom cost item ──
  const addCustomItem = useCallback(
    (tareaIdx: number) => {
      const item = createLineItem('', 'unidades', 0)
      setTareas((prev) => prev.map((t, i) => {
        if (i !== tareaIdx) return t
        const items = [...(t.costos_items ?? []), item]
        return { ...t, costos_items: items }
      }))
      setShowPresets(null)
      setShowOperarios(false)
    },
    [setTareas],
  )

  // ── Remove cost item ──
  const removeCostoItem = useCallback(
    (tareaIdx: number, itemId: string) => {
      setTareas((prev) => prev.map((t, i) => {
        if (i !== tareaIdx) return t
        const items = (t.costos_items ?? []).filter((c) => c.id !== itemId)
        const costoTotal = r2(items.reduce((s, c) => s + c.subtotal, 0))
        const margen = config.margen_global
        const divisor = 1 - margen / 100
        const monto = divisor > 0 ? r2(costoTotal / divisor) : costoTotal
        return { ...t, costos_items: items, monto }
      }))
    },
    [setTareas, config.margen_global],
  )

  // ── Update a line item field and recalculate ──
  const updateLineItem = useCallback(
    (tareaIdx: number, itemId: string, field: 'concepto' | 'cantidad' | 'tarifa' | 'unidad', value: string | number) => {
      setTareas((prev) => prev.map((t, i) => {
        if (i !== tareaIdx) return t
        const items = (t.costos_items ?? []).map((c) => {
          if (c.id !== itemId) return c
          const updated = { ...c, [field]: value }
          updated.subtotal = r2(updated.cantidad * updated.tarifa)
          return updated
        })
        const costoTotal = r2(items.reduce((s, c) => s + c.subtotal, 0))
        const margen = config.margen_global
        const divisor = 1 - margen / 100
        const monto = divisor > 0 ? r2(costoTotal / divisor) : costoTotal
        const horas = r2(items.filter(c => c.unidad === 'horas' || c.unidad === 'sesiones').reduce((s, c) => s + c.cantidad, 0))
        return { ...t, costos_items: items, monto, horas_estimadas: horas || null }
      }))
    },
    [setTareas, config.margen_global],
  )

  // ── Aggregated totals ──
  const totals = useMemo(() => {
    let sumCosto = 0
    let sumVenta = 0
    tareas.forEach((t) => {
      const items = t.costos_items ?? []
      const costo = items.reduce((s, c) => s + c.subtotal, 0)
      sumCosto += costo
      sumVenta += t.monto
    })
    const avgMargen = sumVenta > 0 ? r2((1 - sumCosto / sumVenta) * 100) : 0
    return { costo: r2(sumCosto), venta: r2(sumVenta), avgMargen }
  }, [tareas])

  return (
    <div className="presupuesto-tab">
      {/* ─── Section A: Tarifas Base (collapsible) ─── */}
      <div className={`presupuesto-tarifas${tarifasOpen ? ' open' : ''}`}>
        <button
          type="button"
          className="presupuesto-tarifas__header"
          onClick={() => setTarifasOpen((o) => !o)}
        >
          <h4>
            <IconSettings size={16} />
            Tarifas Base
          </h4>
          {tarifasOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
        </button>

        {tarifasOpen && (
          <div className="presupuesto-tarifas__body">
            <div className="presupuesto-tarifas__grid">
              <div>
                <label>Tarifa Dev/hora</label>
                <input type="number" step="0.01" min="0" value={config.tarifa_dev_hora} onChange={(e) => onConfigChange('tarifa_dev_hora', e.target.value)} />
              </div>
              <div>
                <label>Tarifa QA/hora</label>
                <input type="number" step="0.01" min="0" value={config.tarifa_qa_hora} onChange={(e) => onConfigChange('tarifa_qa_hora', e.target.value)} />
              </div>
              <div>
                <label>Margen Global %</label>
                <input type="number" step="1" min="0" max="99" value={config.margen_global} onChange={(e) => onConfigChange('margen_global', e.target.value)} />
              </div>
              <div>
                <label>Precio Input/MTok</label>
                <input type="number" step="0.01" min="0" value={config.precio_input_mtok} onChange={(e) => onConfigChange('precio_input_mtok', e.target.value)} />
              </div>
              <div>
                <label>Precio Thinking/MTok</label>
                <input type="number" step="0.01" min="0" value={config.precio_thinking_mtok} onChange={(e) => onConfigChange('precio_thinking_mtok', e.target.value)} />
              </div>
              <div>
                <label>Precio Output/MTok</label>
                <input type="number" step="0.01" min="0" value={config.precio_output_mtok} onChange={(e) => onConfigChange('precio_output_mtok', e.target.value)} />
              </div>
            </div>

            {perfiles.length > 0 && (
              <div className="presupuesto-tarifas__profiles">
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginRight: 4 }}>Perfiles:</span>
                {perfiles.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="presupuesto-tarifas__profile-chip"
                    title={`Aplicar ${p.rol_comercial} ${p.seniority} $${p.costo_interno_hora}/h`}
                    onClick={() => fillTarifa(p)}
                  >
                    {p.rol_comercial} {p.seniority} ${p.costo_interno_hora}/h
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Section B: Task Cost Boxes ─── */}
      <div className="presupuesto-tasks">
        {tareas.map((tarea, idx) => {
          const items = tarea.costos_items ?? []
          const costoTotal = r2(items.reduce((s, c) => s + c.subtotal, 0))
          const isExpanded = expandedTarea === idx
          const isPresetsOpen = showPresets === idx

          return (
            <div key={idx} className={`presupuesto-task${isExpanded ? ' presupuesto-task--open' : ''}`}>
              {/* Task header (click to expand) */}
              <button
                type="button"
                className="presupuesto-task__header"
                onClick={() => setExpandedTarea(isExpanded ? null : idx)}
              >
                <div className="presupuesto-task__info">
                  <span className="presupuesto-task__num">{idx + 1}</span>
                  <span className="presupuesto-task__title">{tarea.titulo || 'Sin título'}</span>
                  {items.length > 0 && (
                    <span className="presupuesto-task__badge">{items.length} costo{items.length !== 1 ? 's' : ''}</span>
                  )}
                </div>
                <div className="presupuesto-task__amounts">
                  <span className="presupuesto-task__costo">
                    <IconDollar size={12} />
                    Costo {formatCurrency(costoTotal, moneda)}
                  </span>
                  <span className="presupuesto-task__venta">{formatCurrency(tarea.monto, moneda)}</span>
                  <span style={{ transition: 'transform 0.2s', display: 'inline-flex', transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
                    <IconChevronDown size={14} />
                  </span>
                </div>
              </button>

              {/* Expanded cost items */}
              {isExpanded && (
                <div className="presupuesto-task__body">
                  {items.length > 0 && (
                    <table className="presupuesto-items-table">
                      <thead>
                        <tr>
                          <th>Concepto</th>
                          <th style={{ width: 80 }}>Cantidad</th>
                          <th style={{ width: 80 }}>Unidad</th>
                          <th style={{ width: 90 }}>Tarifa</th>
                          <th style={{ width: 100 }}>Subtotal</th>
                          <th style={{ width: 36 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <input
                                type="text"
                                className="presupuesto-cell-text"
                                value={item.concepto}
                                onChange={(e) => updateLineItem(idx, item.id, 'concepto', e.target.value)}
                                placeholder="Concepto..."
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="presupuesto-cell-input"
                                step="0.5"
                                min="0"
                                value={item.cantidad}
                                onChange={(e) => updateLineItem(idx, item.id, 'cantidad', parseFloat(e.target.value) || 0)}
                              />
                            </td>
                            <td>
                              <select
                                className="presupuesto-cell-select presupuesto-cell-unit"
                                value={item.unidad}
                                onChange={(e) => updateLineItem(idx, item.id, 'unidad', e.target.value)}
                              >
                                <option value="horas">Horas</option>
                                <option value="MTok">MTok</option>
                                <option value="unidades">Unidades</option>
                                <option value="sesiones">Sesiones</option>
                              </select>
                            </td>
                            <td>
                              <input
                                type="number"
                                className="presupuesto-cell-input"
                                step="0.01"
                                min="0"
                                value={item.tarifa}
                                onChange={(e) => updateLineItem(idx, item.id, 'tarifa', parseFloat(e.target.value) || 0)}
                              />
                            </td>
                            <td className="presupuesto-cell-subtotal">
                              {formatCurrency(item.subtotal, moneda)}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="presupuesto-item-remove"
                                onClick={() => removeCostoItem(idx, item.id)}
                                title="Eliminar"
                              >
                                <IconTrash size={13} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>Total Costos</td>
                          <td className="presupuesto-cell-subtotal" style={{ fontWeight: 700 }}>{formatCurrency(costoTotal, moneda)}</td>
                          <td></td>
                        </tr>
                        <tr>
                          <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600, color: 'var(--success)' }}>
                            Precio Venta (margen {config.margen_global}%)
                          </td>
                          <td className="presupuesto-cell-subtotal" style={{ fontWeight: 700, color: 'var(--success)' }}>
                            {formatCurrency(tarea.monto, moneda)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  )}

                  {items.length === 0 && (
                    <div className="presupuesto-task__empty">
                      No hay costos asignados a esta tarea. Agrega conceptos de costo.
                    </div>
                  )}

                  {/* Add cost button + presets dropdown */}
                  <div className="presupuesto-task__actions">
                    <div style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => setShowPresets(isPresetsOpen ? null : idx)}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <IconPlus size={12} /> Agregar Costo
                      </button>

                      {isPresetsOpen && (
                        <div className="presupuesto-presets-dropdown">
                          {/* Operario category with profile sub-menu */}
                          <div style={{ position: 'relative' }}>
                            <button
                              type="button"
                              className={`presupuesto-presets-dropdown__item${showOperarios ? ' presupuesto-presets-dropdown__item--active' : ''}`}
                              onClick={() => setShowOperarios(!showOperarios)}
                            >
                              Operario
                              <IconChevronRight size={12} />
                            </button>
                            {showOperarios && (
                              <div className="presupuesto-presets-submenu">
                                {perfiles.map((p) => (
                                  <button
                                    key={p.id}
                                    type="button"
                                    className="presupuesto-presets-dropdown__item"
                                    onClick={() => addOperarioItem(idx, p)}
                                  >
                                    {p.rol_comercial} {p.seniority}
                                    <span className="presupuesto-presets-dropdown__hint">
                                      ${p.costo_interno_hora}/h
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="presupuesto-presets-dropdown__divider" />
                          {PRESETS.map((p) => (
                            <button
                              key={p.concepto}
                              type="button"
                              className="presupuesto-presets-dropdown__item"
                              onClick={() => addCostoItem(idx, p.concepto, p.unidad, p.tarifaKey)}
                            >
                              {p.concepto}
                              {p.tarifaKey && (
                                <span className="presupuesto-presets-dropdown__hint">
                                  ${config[p.tarifaKey]}/{p.unidad === 'MTok' ? 'MTok' : 'h'}
                                </span>
                              )}
                            </button>
                          ))}
                          <div className="presupuesto-presets-dropdown__divider" />
                          <button
                            type="button"
                            className="presupuesto-presets-dropdown__item presupuesto-presets-dropdown__item--custom"
                            onClick={() => addCustomItem(idx)}
                          >
                            <IconPlus size={12} /> Concepto personalizado
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {tareas.length === 0 && (
          <div className="presupuesto-task__empty" style={{ padding: 32, textAlign: 'center' }}>
            No hay tareas. Agrega tareas en la pestaña Tareas.
          </div>
        )}
      </div>

      {/* ─── Section C: Financial Summary ─── */}
      <div className="presupuesto-summary">
        <div className="presupuesto-summary__card">
          <div className="presupuesto-summary__label"><IconDollar size={14} /> Costo Total</div>
          <div className="presupuesto-summary__value">{formatCurrency(totals.costo, moneda)}</div>
        </div>
        <div className="presupuesto-summary__card">
          <div className="presupuesto-summary__label"><IconTrendingUp size={14} /> Margen Promedio</div>
          <div className="presupuesto-summary__value presupuesto-summary__value--success">{totals.avgMargen}%</div>
        </div>
        <div className="presupuesto-summary__card">
          <div className="presupuesto-summary__label"><IconDollar size={14} /> Precio Total</div>
          <div className="presupuesto-summary__value presupuesto-summary__value--primary">{formatCurrency(totals.venta, moneda)}</div>
        </div>
      </div>

      <div className="presupuesto-financiero">
        <div className="presupuesto-financiero__row"><span>Subtotal</span><strong>{formatCurrency(subtotal, moneda)}</strong></div>
        {igvPct > 0 && <div className="presupuesto-financiero__row"><span>IGV ({igvPct}%)</span><strong>{formatCurrency(igvMonto, moneda)}</strong></div>}
        {descPct > 0 && <div className="presupuesto-financiero__row"><span>Descuento ({descPct}%)</span><strong>-{formatCurrency(descMonto, moneda)}</strong></div>}
        <div className="presupuesto-financiero__total"><span>TOTAL</span><strong>{formatCurrency(total, moneda)}</strong></div>
      </div>
    </div>
  )
}
